/**
 * Custom Product Options Handler - CORRECTED VERSION
 * Manages writing style and packing choice selections with pricing
 * SECURITY: Includes proper validation and error handling
 * PERFORMANCE: Optimized DOM queries and event handling
 */
class CustomProductOptions {
  constructor() {
    this.basePrice = 0;
    this.currentPackingPrice = 0;
    this.currencyFormat = 'USD';
    this.isInitialized = false;
    
    // PERFORMANCE: Cache DOM elements to avoid repeated queries
    this.priceElements = null;
    this.productForm = null;
    
    this.init();
  }

  init() {
    try {
      this.cacheElements();
      this.getBasePrice();
      this.bindEvents();
      this.updateInitialPrice();
      this.isInitialized = true;
      console.log('CustomProductOptions initialized successfully');
    } catch (error) {
      console.error('Failed to initialize CustomProductOptions:', error);
    }
  }

  // PERFORMANCE: Cache frequently used DOM elements
  cacheElements() {
    this.priceElements = {
      sale: document.querySelectorAll('.price-item--sale'),
      regular: document.querySelectorAll('.price-item--regular')
    };
    
    this.productForm = document.querySelector('form[data-type="add-to-cart-form"]') || 
                      document.querySelector('product-form form');
  }

  getBasePrice() {
    // SECURITY: Safe element access with proper validation
    const priceElement = document.querySelector('.price-item--sale, .price-item--regular');
    if (!priceElement) {
      console.warn('Price element not found, using default base price');
      this.basePrice = 0;
      return;
    }

    try {
      const priceText = priceElement.textContent.trim();
      // SECURITY: Sanitize and validate price extraction
      const match = priceText.match(/[\d,]+\.?\d*/);
      if (match && match[0]) {
        const price = parseFloat(match[0].replace(/,/g, ''));
        if (!isNaN(price) && price >= 0) {
          this.basePrice = price * 100; // Convert to cents
        } else {
          console.warn('Invalid price detected, using fallback');
          this.basePrice = 0;
        }
      } else {
        console.warn('Could not extract price from text:', priceText);
        this.basePrice = 0;
      }
    } catch (error) {
      console.error('Error extracting base price:', error);
      this.basePrice = 0;
    }
  }

  bindEvents() {
    // PERFORMANCE: More efficient event delegation
    document.addEventListener('change', (e) => {
      if (e.target && e.target.matches('.custom-option-input')) {
        this.handleOptionChange(e.target);
      }
    });

    // PERFORMANCE: Debounced variant change handler
    let variantChangeTimeout;
    document.addEventListener('variant:change', () => {
      clearTimeout(variantChangeTimeout);
      variantChangeTimeout = setTimeout(() => {
        this.cacheElements(); // Refresh cached elements
        this.getBasePrice();
        this.updateDisplayPrice();
      }, 100);
    });
  }

  handleOptionChange(input) {
    // SECURITY: Validate input element
    if (!input || !input.value || !input.name) {
      console.warn('Invalid option input:', input);
      return;
    }

    const optionValue = input.value;
    const optionName = input.name;
    
    try {
      // Handle packing choice price updates
      if (optionName === 'properties[Packing Choice]') {
        this.updatePackingPrice(input);
      }
      
      // Add visual feedback
      this.addSelectionFeedback(input);
      
      console.log(`Selected ${optionName}: ${optionValue}`);
    } catch (error) {
      console.error('Error handling option change:', error);
    }
  }

  updatePackingPrice(selectedInput) {
    // SECURITY: Validate and sanitize price data
    try {
      const priceData = selectedInput.dataset.price;
      const additionalPrice = priceData ? parseInt(priceData, 10) : 0;
      
      // SECURITY: Validate price is a safe number
      if (isNaN(additionalPrice) || additionalPrice < 0) {
        console.warn('Invalid price data:', priceData);
        this.currentPackingPrice = 0;
      } else {
        this.currentPackingPrice = additionalPrice;
      }
      
      // Update the displayed price
      this.updateDisplayPrice();
      
      // Add price information to form for cart
      this.addPriceToForm(this.currentPackingPrice);
    } catch (error) {
      console.error('Error updating packing price:', error);
      this.currentPackingPrice = 0;
      this.updateDisplayPrice();
    }
  }

  updateDisplayPrice() {
    // SECURITY: Validate base price before calculation
    if (!this.isInitialized || this.basePrice < 0) {
      console.warn('Cannot update display price: invalid base price');
      return;
    }

    try {
      const totalPrice = this.basePrice + this.currentPackingPrice;
      const formattedPrice = this.formatPrice(totalPrice);
      
      // PERFORMANCE: Use cached elements
      if (this.priceElements.sale.length > 0) {
        this.priceElements.sale.forEach(element => {
          if (element) element.textContent = formattedPrice;
        });
      } else if (this.priceElements.regular.length > 0) {
        this.priceElements.regular.forEach(element => {
          if (element) element.textContent = formattedPrice;
        });
      } else {
        console.warn('No price elements found to update');
      }
    } catch (error) {
      console.error('Error updating display price:', error);
    }
  }

  formatPrice(priceInCents) {
    // SECURITY: Validate input
    if (!priceInCents || isNaN(priceInCents) || priceInCents < 0) {
      console.warn('Invalid price for formatting:', priceInCents);
      return '$0.00';
    }

    try {
      const price = priceInCents / 100;
      
      // PERFORMANCE: Use cached price element reference
      const existingPrice = document.querySelector('.price-item--sale, .price-item--regular');
      if (existingPrice) {
        const existingText = existingPrice.textContent.trim();
        if (existingText.includes('$')) {
          return `$${price.toFixed(2)}`;
        } else if (existingText.includes('₪')) {
          return `₪${price.toFixed(2)}`;
        } else if (existingText.includes('€')) {
          return `€${price.toFixed(2)}`;
        }
      }
      
      // Default to USD format
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(price);
    } catch (error) {
      console.error('Error formatting price:', error);
      return '$0.00';
    }
  }

  addPriceToForm(additionalPrice) {
    // SECURITY: Validate form exists and price is safe
    if (!this.productForm) {
      console.warn('Product form not found, cannot add price field');
      return;
    }

    if (additionalPrice && (isNaN(additionalPrice) || additionalPrice < 0)) {
      console.warn('Invalid additional price:', additionalPrice);
      return;
    }

    try {
      // Remove existing price field
      const existingPriceField = this.productForm.querySelector('input[name="properties[_packing_fee]"]');
      if (existingPriceField) {
        existingPriceField.remove();
      }

      // Add new price field (only if there's an additional fee)
      if (additionalPrice && additionalPrice > 0) {
        const priceField = document.createElement('input');
        priceField.type = 'hidden';
        priceField.name = 'properties[_packing_fee]';
        // SECURITY: Sanitize the value
        priceField.value = `${Math.round(additionalPrice)} cents`;
        this.productForm.appendChild(priceField);
      }
    } catch (error) {
      console.error('Error adding price to form:', error);
    }
  }

  updateInitialPrice() {
    // SECURITY: Safe element detection with proper fallbacks
    try {
      const defaultPackingChoice = document.querySelector('input[name="properties[Packing Choice]"]:checked');
      if (defaultPackingChoice) {
        this.updatePackingPrice(defaultPackingChoice);
      } else {
        // Reset pricing if no packing options available
        this.currentPackingPrice = 0;
        this.updateDisplayPrice();
        console.log('No default packing choice found, pricing reset');
      }
    } catch (error) {
      console.error('Error setting initial price:', error);
      this.currentPackingPrice = 0;
      this.updateDisplayPrice();
    }
  }

  addSelectionFeedback(input) {
    // SECURITY: Validate input element
    if (!input || !input.nextElementSibling) {
      return;
    }

    try {
      const label = input.nextElementSibling;
      if (label && label.style) {
        label.style.transform = 'scale(0.95)';
        setTimeout(() => {
          if (label.style) {
            label.style.transform = '';
          }
        }, 150);
      }
    } catch (error) {
      console.error('Error adding selection feedback:', error);
    }
  }
}

// CORRECTED: Secure and efficient initialization
function initializeCustomOptions() {
  // SECURITY: Only initialize if custom options are present
  const customOptionsContainer = document.querySelector('.custom-product-options');
  if (customOptionsContainer) {
    try {
      const writing = customOptionsContainer.getAttribute('data-writing-enabled');
      const packing = customOptionsContainer.getAttribute('data-packing-enabled');
      console.log('[CustomProductOptions][DEBUG] container found. writing:', writing, 'packing:', packing);
    } catch(e){ console.warn('[CustomProductOptions][DEBUG] failed reading data attributes', e); }
    try {
      new CustomProductOptions();
    } catch (error) {
      console.error('Failed to initialize custom product options:', error);
    }
  } else {
    console.log('[CustomProductOptions][DEBUG] No custom options container present. Skipping initialization');
  }
}

// PERFORMANCE: Efficient DOM ready detection
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCustomOptions);
} else {
  initializeCustomOptions();
}
